<template>
  <section
    class="orcamento-executed-section"
    aria-labelledby="titulo-executado"
  >
    <header class="orcamento-executed-header">
      <div class="orcamento-executed-heading">
        <div class="orcamento-executed-step" aria-hidden="true">2</div>
        <div>
          <div class="orcamento-executed-eyebrow">Realização</div>
          <h3 id="titulo-executado" class="orcamento-executed-title">
            O que foi executado
          </h3>
          <p class="orcamento-executed-description">
            Despesas registradas durante a utilização do orçamento.
          </p>
        </div>
      </div>
      <div class="orcamento-executed-total">
        <span>Valor executado</span>
        <strong>{{ valorExecutado | formatoMonetario }}</strong>
      </div>
    </header>

    <div class="orcamento-executed-body">
      <div class="orcamento-executed-table-heading">
        <div>
          <h4>Lançamentos realizados</h4>
          <p>
            {{ recursosDia.length }} lançamento(s). Expanda uma linha para ver
            comprovantes e localização.
          </p>
        </div>
        <v-btn
          v-if="podeEditar"
          :disabled="!buttonSave"
          :loading="loading"
          class="
            ma-1
            font-weight-bold
            text-none
            btn-primary
            bg-gradient-primary
            px-6
            ms-3
          "
          height="39"
          @click="update"
        >
          <v-icon left>mdi-content-save-outline</v-icon>
          Salvar alterações
        </v-btn>
      </div>

      <v-dialog v-model="dialog" max-width="500px">
        <v-card>
          <v-card-title>
            <span class="text-h5">Editar valor executado</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field-money
                    v-model="editedItem.valor"
                    v-bind:label="$t('Valor')"
                    v-bind:properties="{
                      prefix: 'R$',
                      readonly: false,
                      disabled: false,
                      outlined: false,
                      clearable: true,
                    }"
                    v-bind:options="{
                      locale: 'pt-BR',
                      length: 12,
                      precision: 2,
                      empty: '0',
                    }"
                    class="font-size-input text-light-input placeholder-light"
                    dense
                    flat
                    filled
                    solo
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="px-6 pb-5">
            <v-spacer />
            <v-btn
              class="
                ma-1
                font-weight-bold
                text-capitalize
                btn-ls btn-secondary
                bg-gradient-light
                px-6
              "
              height="39"
              @click="close"
            >
              {{ $t("Cancelar") }}
            </v-btn>
            <v-btn
              class="
                ma-1
                font-weight-bold
                text-none
                btn-primary
                bg-gradient-primary
                px-6
                ms-3
              "
              height="39"
              @click="save"
            >
              <v-icon left>mdi-content-save-outline</v-icon>
              Confirmar alteração
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogDelete" max-width="500px">
        <v-card>
          <v-card-title class="text-h5">
            {{ $t("Confirma_exclusao?") }}
          </v-card-title>
          <v-card-text>
            O lançamento será removido das despesas executadas. Para concluir,
            salve as alterações do pedido.
          </v-card-text>
          <v-card-actions class="px-6 pb-5">
            <v-spacer />
            <v-btn
              class="
                ma-1
                font-weight-bold
                text-capitalize
                btn-ls btn-secondary
                bg-gradient-light
                px-6
              "
              height="39"
              @click="closeDelete"
            >
              {{ $t("Cancelar") }}
            </v-btn>
            <v-btn
              color="error"
              dark
              class="ma-1 text-none font-weight-bold px-6 ms-3"
              height="39"
              @click="deleteItemConfirm"
            >
              <v-icon left>mdi-delete-outline</v-icon>
              Remover lançamento
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-data-table
        hide-default-footer
        disable-pagination
        :headers="headersExecucao"
        :items="recursosDia"
        :search="searchApuracaoDia"
        :item-class="getItemClass"
        :show-expand="true"
        :single-expand="true"
        class="table orcamento-executed-table"
        @click:row="handleClick"
      >
        <template v-if="podeEditar" v-slot:item.actions="{ item }">
          <div class="orcamento-executed-actions">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  small
                  color="#67748e"
                  class="btn-ls me-0.90 my-1 rounded-sm ma-1"
                  :aria-label="$t('Editar')"
                  @click.stop="editItem(item)"
                >
                  mdi-pencil-outline
                </v-icon>
              </template>
              <span>{{ $t("Editar") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  small
                  color="#67748e"
                  class="btn-ls me-0.90 my-1 rounded-sm ma-1"
                  :aria-label="$t('Excluir')"
                  @click.stop="deleteItem(item)"
                >
                  mdi-delete-outline
                </v-icon>
              </template>
              <span>{{ $t("Excluir") }}</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:[`item.data`]="{ item }">
          {{ item.data | dataParaLeitura }}
        </template>
        <template v-slot:[`item.valor`]="{ item }">
          <strong>{{ item.valor | formatoMonetario }}</strong>
        </template>
        <template v-slot:no-data>
          <div class="orcamento-executed-empty">
            <div class="orcamento-executed-empty__icon">
              <v-icon size="30" color="#0f766e"
                >mdi-receipt-text-outline</v-icon
              >
            </div>
            <strong>Nenhuma despesa executada até o momento.</strong>
            <span>Os lançamentos aparecerão aqui quando forem realizados.</span>
          </div>
        </template>

        <template v-slot:expanded-item="{ item, headers }">
          <td :colspan="headers.length" class="orcamento-execution-detail-cell">
            <v-row dense class="orcamento-execution-detail">
              <v-col cols="12" md="5">
                <div class="orcamento-execution-summary">
                  <div class="orcamento-execution-summary__label">
                    Descrição do lançamento
                  </div>
                  <div class="orcamento-execution-summary__text">
                    {{ item.descricao || "Não informada" }}
                  </div>
                </div>

                <div v-if="possuiComprovantes(item)" class="orcamento-evidence">
                  <div class="orcamento-evidence__title">
                    Comprovantes e evidências
                  </div>
                  <div class="orcamento-evidence__items">
                    <div v-if="item.imagemOperador" class="evidence-item">
                      <span>{{ $t("operador") }}</span>
                      <ShowImage :url="item.imagemOperador">
                        <v-img
                          :src="item.imagemOperador"
                          width="96"
                          height="96"
                          contain
                          class="evidence-image"
                        />
                      </ShowImage>
                    </div>
                    <div v-if="item.imagemDocumento" class="evidence-item">
                      <span>{{ $t("Documento") }}</span>
                      <ShowImage :url="item.imagemDocumento">
                        <v-img
                          :src="item.imagemDocumento"
                          width="96"
                          height="96"
                          contain
                          class="evidence-image"
                        />
                      </ShowImage>
                    </div>
                    <div v-if="item.documentoArquivo" class="evidence-item">
                      <span>{{ $t("Comprovante") }}</span>
                      <v-btn
                        outlined
                        color="primary"
                        class="text-none font-weight-bold evidence-download"
                        @click="downloadDocumento(item.documentoArquivo)"
                      >
                        <v-icon left small>mdi-download-outline</v-icon>
                        Baixar arquivo
                      </v-btn>
                    </div>
                  </div>
                </div>
                <div v-else class="orcamento-no-evidence">
                  <v-icon size="19" color="#98a2b3">
                    mdi-file-document-alert-outline
                  </v-icon>
                  Nenhum comprovante anexado.
                </div>
              </v-col>

              <v-col cols="12" md="7">
                <div class="orcamento-map-heading">
                  <div>
                    <strong>Local da despesa</strong>
                    <span>Posição registrada no lançamento.</span>
                  </div>
                  <v-icon color="#667085">mdi-map-marker-outline</v-icon>
                </div>
                <div class="orcamento-map-wrapper">
                  <CardMapaGoogle :item="item" />
                </div>
              </v-col>
            </v-row>
          </td>
        </template>
      </v-data-table>
    </div>
  </section>
</template>

<script>
import CardMapaGoogle from "@/components/base/proprios/CardMapaGoogle";
import ShowImage from "@/components/base/proprios/ShowImage";
import listas from "@/mixins/listas";
import DataService from "@/services/ComumDataService";
export default {
  name: "CardApuracoesDetalhesOrcamentoDespesa",
  mixins: [listas],
  components: { CardMapaGoogle, ShowImage },
  data() {
    return {
      dialog: false,
      dialogDelete: false,
      buttonSave: false,
      loading: false,
      searchApuracaoDia: "",
      parametrizacaoManualBancoHoras: false,
      parametrizacaoManualBancoHorasTipoDois: false,
      editedIndex: -1,
      editedItem: {},
      defaultItem: {
        valor: 0,
      },
    };
  },

  props: {
    item: { type: Object, required: true },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
  },

  methods: {
    getWeekDayFromString(date) {
      console.log("item", date);

      let data = new Date(date);
      console.log("date", data, data.getDay());

      let dia = data.getDay();
      if (dia == 0) {
        return "Segunda";
      }
      if (dia == 1) {
        return "Terca";
      }
      if (dia == 2) {
        return "Quarta";
      }
      if (dia == 3) {
        return "Quinta";
      }
      if (dia == 4) {
        return "Sexta";
      }
      if (dia == 5) {
        return "Sabado";
      }
      if (dia == 6) {
        return "Domingo";
      }
    },

    editItem(item) {
      this.editedIndex = item;
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    deleteItem(item) {
      this.editedIndex = item;
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      var itemExistente = this.item.items.find(
        (item) => item.id == this.editedItem.id
      );
      // this.recurso.itemsPedido = [];

      if (itemExistente) {
        this.item.items = this.item.items.filter((item) => {
          return item.id !== itemExistente.id;
        });
      }
      this.buttonSave = true;
      this.closeDelete();
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    save() {
      var itemExistente = this.item.items.find(
        (item) => item.id == this.editedItem.id
      );
      // this.recurso.itemsPedido = [];

      if (itemExistente) {
        this.item.items = this.item.items.filter((item) => {
          return item.id !== itemExistente.id;
        });
      }
      this.item.items.push(this.editedItem);
      // } else {
      //   this.recurso.itemspedido.push(this.editedItem);
      //   console.log("edited 2", this.recurso);
      // }
      this.buttonSave = true;
      this.close();
    },

    update() {
      this.loading = true;
      DataService.put("pedidoOrcamentoDespesas/" + this.item.id, this.item)
        .then(() => {
          this.loading = false;
          this.buttonSave = false;
          this.$emit("atualizado");
        })
        .catch((error) => {
          this.loading = false;

          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
          this.erros = error.response.data.errors;
          this.$swal.fire(this.$t("Erro"), this.$t("Tente_novamente"), "error");
        });
    },

    downloadDocumento(documentoArquivo) {
      // Cenário A: Se `documentoArquivo` for uma URL apontando para o servidor
      // (Ex.: https://meus-arquivos/documento.pdf).
      // Basta abrir em nova aba ou forçar o download:
      if (this.isUrl(documentoArquivo)) {
        window.open(documentoArquivo, "_blank");
        return;
      }

      // Cenário B: Se `documentoArquivo` for uma string base64
      // Precisamos criar um Blob e forçar o download.
      // Ex.: "data:application/pdf;base64,JVBERi0xLjQKJ..."
      if (documentoArquivo.startsWith("data:")) {
        const link = document.createElement("a");
        link.href = documentoArquivo;
        // Tente extrair a extensão se tiver "data:application/pdf" ou "data:image/jpeg" etc.
        const extension = this.getExtensionFromBase64(documentoArquivo);
        link.download = `documento${extension}`;
        link.click();
        return;
      }

      // Caso contrário, trate como erro ou algo indefinido
      this.$swal.fire(
        this.$t("Erro"),
        this.$t("Arquivo_invalido_ou_não_suportado"),
        "error"
      );
    },

    isUrl(value) {
      // Bem simples: checa se inicia com http(s) ou termina com .pdf, .png, etc.
      // Se preferir, use uma regex mais robusta.
      return value.startsWith("http://") || value.startsWith("https://");
    },

    getExtensionFromBase64(base64) {
      if (base64.startsWith("data:application/pdf;")) {
        return ".pdf";
      }
      if (base64.startsWith("data:image/png;")) {
        return ".png";
      }
      if (base64.startsWith("data:image/jpeg;")) {
        return ".jpg";
      }
      return "";
    },

    possuiComprovantes(item) {
      return Boolean(
        item.imagemOperador || item.imagemDocumento || item.documentoArquivo
      );
    },
  },

  computed: {
    podeEditar() {
      return !["CONCLUIDO", "CANCELADO"].includes(this.item.statusPedido);
    },

    headersExecucao() {
      const headers = [
        {
          text: this.$t("Data"),
          value: "data",
          sortable: true,
        },
        {
          text: this.$t("Valor"),
          align: "end",
          value: "valor",
          sortable: true,
        },
        {
          text: this.$t("contaFinanceira"),
          value: "contaFinanceira.descricao",
          sortable: true,
        },
        {
          text: this.$t("centroCusto"),
          value: "centroCusto.descricao",
          sortable: true,
        },
      ];
      if (this.podeEditar) {
        headers.push({
          text: "Ações",
          align: "center",
          value: "actions",
          sortable: false,
          width: "112px",
        });
      }
      return headers;
    },

    recursosDia() {
      return Array.isArray(this.item.items) ? this.item.items : [];
    },

    valorExecutado() {
      return this.recursosDia.reduce(
        (total, recurso) => total + Number(recurso.valor || 0),
        0
      );
    },
  },
  mounted() {},
};
</script>

<style scoped>
.orcamento-executed-section {
  overflow: hidden;
  border: 1px solid #d9e7e3;
  border-top: 4px solid #0f766e;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(52, 71, 103, 0.07);
}

.orcamento-executed-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 18px 20px;
  border-bottom: 1px solid #deebe7;
  background: linear-gradient(90deg, #eef9f6 0%, #fff 68%);
}

.orcamento-executed-heading {
  display: flex;
  align-items: center;
  gap: 12px;
}

.orcamento-executed-step {
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #0f766e;
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
}

.orcamento-executed-eyebrow {
  color: #56756f;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1.4;
  text-transform: uppercase;
}

.orcamento-executed-title {
  margin: 0;
  color: #344767;
  font-size: 1.08rem;
  font-weight: 800;
  line-height: 1.35;
}

.orcamento-executed-description {
  margin: 2px 0 0;
  color: #67748e;
  font-size: 0.875rem;
  line-height: 1.4;
}

.orcamento-executed-total {
  min-width: 170px;
  padding-left: 20px;
  border-left: 1px solid #cfe3de;
  text-align: right;
}

.orcamento-executed-total span,
.orcamento-executed-total strong {
  display: block;
}

.orcamento-executed-total span {
  color: #667085;
  font-size: 0.75rem;
  font-weight: 700;
}

.orcamento-executed-total strong {
  color: #0f766e;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.35;
}

.orcamento-executed-body {
  padding: 20px;
}

.orcamento-executed-table-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.orcamento-executed-table-heading h4 {
  margin: 0;
  color: #344767;
  font-size: 0.95rem;
  font-weight: 800;
}

.orcamento-executed-table-heading p {
  margin: 2px 0 0;
  color: #67748e;
  font-size: 0.8125rem;
}

.orcamento-executed-table {
  overflow: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 9px;
}

.orcamento-executed-actions {
  display: inline-flex;
  align-items: center;
}

.orcamento-executed-empty {
  display: flex;
  min-height: 150px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 4px;
  color: #667085;
}

.orcamento-executed-empty__icon {
  display: inline-flex;
  width: 52px;
  height: 52px;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  border-radius: 50%;
  background: #ecf8f5;
}

.orcamento-executed-empty strong {
  color: #344767;
  font-size: 0.9rem;
}

.orcamento-executed-empty span {
  font-size: 0.8125rem;
}

.orcamento-execution-detail-cell {
  padding: 0 !important;
  background: #f8fafc;
}

.orcamento-execution-detail {
  padding: 16px;
}

.orcamento-execution-summary,
.orcamento-evidence,
.orcamento-no-evidence,
.orcamento-map-heading,
.orcamento-map-wrapper {
  border: 1px solid #e4e7ec;
  border-radius: 9px;
  background: #fff;
}

.orcamento-execution-summary {
  padding: 14px;
}

.orcamento-execution-summary__label,
.orcamento-evidence__title {
  color: #667085;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.orcamento-execution-summary__text {
  margin-top: 4px;
  color: #344767;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.orcamento-evidence {
  margin-top: 10px;
  padding: 14px;
}

.orcamento-evidence__items {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
}

.evidence-item {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 6px;
}

.evidence-item > span {
  color: #667085;
  font-size: 0.75rem;
  font-weight: 700;
}

.evidence-image {
  overflow: hidden;
  border: 1px solid #dfe5ec;
  border-radius: 8px;
  background: #f8fafc;
  cursor: pointer;
}

.evidence-download {
  height: 40px !important;
}

.orcamento-no-evidence {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 10px;
  padding: 12px 14px;
  color: #667085;
  font-size: 0.8125rem;
}

.orcamento-map-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 13px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

.orcamento-map-heading strong,
.orcamento-map-heading span {
  display: block;
}

.orcamento-map-heading strong {
  color: #344767;
  font-size: 0.875rem;
}

.orcamento-map-heading span {
  margin-top: 1px;
  color: #667085;
  font-size: 0.75rem;
}

.orcamento-map-wrapper {
  overflow: hidden;
  min-height: 260px;
  border-top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

@media (max-width: 959px) {
  .orcamento-executed-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .orcamento-executed-total {
    width: 100%;
    padding-top: 12px;
    padding-left: 0;
    border-top: 1px solid #cfe3de;
    border-left: 0;
    text-align: left;
  }
}

@media (max-width: 599px) {
  .orcamento-executed-header,
  .orcamento-executed-body {
    padding: 16px;
  }

  .orcamento-executed-table-heading {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
